import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDeletePricingToolDraft,
  useGetPricingToolDraft,
  useSavePricingToolDraft,
} from '~/hooks/apis/offers/usePricingToolDraft';

import { useBlackouts } from './tabs/blackouts/useBlackouts';
import { useEgsDrafts } from './tabs/egs/useEgsDrafts';
import { useForecastingState } from './tabs/forecasting/useForecastingState';
import { useInclusionDrafts } from './tabs/inclusions/useInclusionDrafts';
import { useOverrides } from './tabs/overrides/useOverrides';
import { usePackageOptionsState } from './tabs/packageOptions/usePackageOptionsState';
import { netRateFor } from './tabs/propertySetup/propertySetupModel';
import { usePropertySetup } from './tabs/propertySetup/usePropertySetup';
import { toStayInputs } from './tabs/scrape/stayInputs';
import { useScrapeSession } from './tabs/scrape/useScrapeSession';
import { useSurchargeGrids } from './tabs/surcharges/useSurchargeGrids';
import type { PricingOptionIndex } from './types';

// Mounted once at PricingToolPage so state here survives tab switches - including
// useScrapeSession's poll timer, whose cleanup now fires only when this unmounts, not per-tab.
interface PricingToolSessionValue {
  egs: ReturnType<typeof useEgsDrafts>;
  inclusions: ReturnType<typeof useInclusionDrafts>;
  propertySetup: ReturnType<typeof usePropertySetup>;
  blackouts: ReturnType<typeof useBlackouts>;
  overrides: ReturnType<typeof useOverrides>;
  scrape: ReturnType<typeof useScrapeSession>;
  packageOptions: ReturnType<typeof usePackageOptionsState>;
  preferredOption: PricingOptionIndex | null;
  setPreferredOption: (preferredOption: PricingOptionIndex | null) => void;
  surcharges: ReturnType<typeof useSurchargeGrids>;
  forecasting: ReturnType<typeof useForecastingState>;
  saveDraft: () => Promise<unknown>;
  isSavingDraft: boolean;
  deleteDraft: () => Promise<unknown>;
  isDeletingDraft: boolean;
  hasDraft: boolean;
}

const PricingToolSessionContext = createContext<PricingToolSessionValue | undefined>(undefined);

export function PricingToolSessionProvider({ offerId, children }: { offerId?: string; children: React.ReactNode }) {
  const inclusions = useInclusionDrafts(offerId);
  const egs = useEgsDrafts(offerId);
  const propertySetup = usePropertySetup(offerId);
  const blackouts = useBlackouts(offerId);
  const scrape = useScrapeSession(offerId);
  const packageOptions = usePackageOptionsState(offerId);
  const overrides = useOverrides(offerId, packageOptions.activeOption);
  const surcharges = useSurchargeGrids(offerId, scrape.stayInputs?.currencyCode);
  const forecasting = useForecastingState(offerId);

  const netRate = propertySetup.fx.rate;
  const lastNetRate = useRef<number | undefined>(undefined);
  const { reapplyNetFormulaAtRate } = packageOptions;
  useEffect(() => {
    lastNetRate.current = undefined;
  }, [offerId]);
  useEffect(() => {
    if (!netRate || netRate <= 0) {
      return;
    }
    if (lastNetRate.current !== undefined && lastNetRate.current !== netRate) {
      reapplyNetFormulaAtRate({ fromRate: lastNetRate.current, toRate: netRate });
    }
    lastNetRate.current = netRate;
  }, [netRate, reapplyNetFormulaAtRate]);

  const [preferredOption, setPreferredOption] = useState<PricingOptionIndex | null>(null);
  useEffect(() => setPreferredOption(null), [offerId]);

  // version starts at 0 (no draft yet - PUT creates). Bumped from whatever the GET or the
  // last successful PUT reported, so concurrent-edit conflicts (409) surface on the next save.
  const draftVersionRef = useRef(0);
  const [hasDraft, setHasDraft] = useState(false);

  const { data: loadedDraft } = useGetPricingToolDraft(offerId);
  const loadedForOfferId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (loadedDraft === undefined || loadedForOfferId.current === offerId) {
      return;
    }
    loadedForOfferId.current = offerId;

    if (!loadedDraft) {
      draftVersionRef.current = 0;
      setHasDraft(false);
      return;
    }

    draftVersionRef.current = loadedDraft.version;
    setHasDraft(true);
    const { payload } = loadedDraft;

    if (payload.propertySetup) {
      propertySetup.loadValues(payload.propertySetup);
      lastNetRate.current = netRateFor(payload.propertySetup);
    }
    if (payload.blackouts) {
      const now = new Date().toISOString();
      blackouts.setBlackouts(payload.blackouts.map((draft) => ({ ...draft, createdAt: now, updatedAt: now })));
    }
    if (payload.overrides) {
      overrides.setOverrides(payload.overrides);
    }
    if (payload.scrape) {
      scrape.loadDraft(payload.scrape, payload.propertySetup);
    }
    if (payload.surcharges) {
      const draftCurrencyCode = payload.scrape
        ? toStayInputs(payload.scrape, payload.propertySetup).currencyCode
        : propertySetup.values?.currency;
      surcharges.loadDraft(payload.surcharges, draftCurrencyCode);
    }
    if (payload.packageOptions) {
      packageOptions.loadDraft(payload.packageOptions);
    }
    if (payload.inclusions) {
      inclusions.loadDraft(payload.inclusions);
    }
    if (payload.forecasting) {
      forecasting.loadDraft(payload.forecasting);
    }
    if (payload.egs) {
      egs.loadDraft(payload.egs);
    }
    setPreferredOption(payload.preferredOption ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedDraft, offerId]);

  const saveDraftMutation = useSavePricingToolDraft(offerId);
  const saveDraft = useCallback(
    () =>
      saveDraftMutation
        .mutateAsync({
          version: draftVersionRef.current,
          payload: {
            propertySetup: propertySetup.values ?? undefined,
            blackouts: blackouts.blackouts.map(({ id, name, startDate, endDate }) => ({
              id,
              name,
              startDate,
              endDate,
            })),
            overrides: overrides.overrides,
            scrape: scrape.stayInputs
              ? {
                  nights: scrape.stayInputs.nights,
                  baseSellPriceAud: scrape.stayInputs.baseSellPriceAud,
                  prvInclusionsAud: scrape.stayInputs.prvInclusionsAud,
                  jobId: scrape.jobId,
                  discountPct: scrape.discountPct,
                  rateShop: scrape.rateShopInputs,
                }
              : undefined,
            preferredOption,
            surcharges: surcharges.session,
            packageOptions: {
              optionStates: packageOptions.optionStates,
              roomAssets: packageOptions.roomAssets,
            },
            inclusions: inclusions.drafts.map(({ createdAt: _createdAt, updatedAt: _updatedAt, ...draft }) => draft),
            forecasting: forecasting.session,
            egs: egs.drafts.map(
              ({ createdAt: _createdAt, updatedAt: _updatedAt, sourceName: _sourceName, ...draft }) => draft,
            ),
          },
        })
        .then((draft) => {
          draftVersionRef.current = draft.version;
          setHasDraft(true);
          return draft;
        }),
    [
      saveDraftMutation,
      propertySetup.values,
      blackouts.blackouts,
      overrides.overrides,
      scrape.stayInputs,
      scrape.jobId,
      scrape.discountPct,
      scrape.rateShopInputs,
      preferredOption,
      surcharges.session,
      packageOptions.optionStates,
      packageOptions.roomAssets,
      inclusions.drafts,
      forecasting.session,
      egs.drafts,
    ],
  );

  const deleteDraftMutation = useDeletePricingToolDraft(offerId);
  const deleteDraft = useCallback(
    () =>
      deleteDraftMutation.mutateAsync(draftVersionRef.current).then((result) => {
        setTimeout(() => window.location.reload(), 300);
        return result;
      }),
    [deleteDraftMutation],
  );

  const value = useMemo(
    () => ({
      egs,
      inclusions,
      propertySetup,
      blackouts,
      overrides,
      scrape,
      packageOptions,
      preferredOption,
      setPreferredOption,
      surcharges,
      forecasting,
      saveDraft,
      isSavingDraft: saveDraftMutation.isPending,
      deleteDraft,
      isDeletingDraft: deleteDraftMutation.isPending,
      hasDraft,
    }),
    [
      egs,
      inclusions,
      propertySetup,
      blackouts,
      overrides,
      scrape,
      packageOptions,
      preferredOption,
      surcharges,
      forecasting,
      saveDraft,
      saveDraftMutation.isPending,
      deleteDraft,
      deleteDraftMutation.isPending,
      hasDraft,
    ],
  );

  return <PricingToolSessionContext.Provider value={value}>{children}</PricingToolSessionContext.Provider>;
}

export function usePricingToolSession() {
  const context = useContext(PricingToolSessionContext);

  if (!context) {
    throw new Error('usePricingToolSession must be used within a PricingToolSessionProvider.');
  }

  return context;
}

export function useInclusionDraftsContext() {
  return usePricingToolSession().inclusions;
}

export function useEgsDraftsContext() {
  return usePricingToolSession().egs;
}

export function usePropertySetupContext() {
  return usePricingToolSession().propertySetup;
}

export function useBlackoutsContext() {
  return usePricingToolSession().blackouts;
}

export function useOverridesContext() {
  return usePricingToolSession().overrides;
}

export function useScrapeSessionContext() {
  return usePricingToolSession().scrape;
}

export function usePackageOptionsContext() {
  return usePricingToolSession().packageOptions;
}

export function usePreferredOptionContext() {
  const { preferredOption, setPreferredOption } = usePricingToolSession();
  return { preferredOption, setPreferredOption };
}

export function useSurchargeGridsContext() {
  return usePricingToolSession().surcharges;
}

export function useForecastingContext() {
  return usePricingToolSession().forecasting;
}

export function useSaveDraftContext() {
  const { saveDraft, isSavingDraft } = usePricingToolSession();
  return { saveDraft, isSavingDraft };
}

export function useDeleteDraftContext() {
  const { deleteDraft, isDeletingDraft, hasDraft } = usePricingToolSession();
  return { deleteDraft, isDeletingDraft, hasDraft };
}
